import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import {
  TICKET_PRIORITIES,
  TicketDetail,
  TicketPriority,
  createTicket,
  ticketErrorMessage,
} from "../../../utils/icTicket";
import { useViewport } from "../../../utils/useViewport";
import { PRIMARY, priorityLabel } from "./ticketStatus";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (ticket: TicketDetail) => void;
};

const NewTicketDialog = ({ open, onClose, onCreated }: Props) => {
  const { isPhone } = useViewport();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("MEDIUM");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh form every time the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setPriority("MEDIUM");
      setError(null);
      setSaving(false);
    }
  }, [open]);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const ticket = await createTicket({
        title: title.trim(),
        description: description.trim(),
        priority,
      });
      onCreated(ticket);
    } catch (err) {
      setError(ticketErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
      aria-labelledby="new-ticket-title"
    >
      <DialogTitle id="new-ticket-title" sx={{ fontWeight: 700 }}>
        New support ticket
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="Title"
            placeholder="Short summary of the problem"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
            disabled={saving}
          />
          <TextField
            label="Description"
            placeholder="What happened, where in Thrive, and what you expected instead"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            multiline
            minRows={5}
            fullWidth
            disabled={saving}
          />
          <FormControl fullWidth disabled={saving}>
            <InputLabel id="new-ticket-priority-label">Priority</InputLabel>
            <Select
              labelId="new-ticket-priority-label"
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              {TICKET_PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {priorityLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
        >
          {saving ? "Submitting…" : "Submit ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTicketDialog;
